import { AfterViewInit, Component, computed, inject, Injector, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatDivider, MatSelectionList } from '@angular/material/list';
import { MatTabGroup } from '@angular/material/tabs';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { OPTION_BLANK } from '@common/enums/constants';
import { GlobalTablesService } from '@common/services/global-tables-service';
import {
    FullscreenActions,
    OpenFullscreenModalActions,
    selectCurrentRecord,
    selectCurrentStoryIndex,
    selectCurrentStoryParentTopic,
    selectIsCurrentRecordFromPin,
    selectIsCurrentStoryStandalone,
    selectLoaderState,
    selectNewRecordCreationInProgress,
} from '@common/store/fullscreen';
import { StoryHttpActions } from '@common/store/story/actions/story.actions';
import { Prompt } from '@common/store/story/models/story.models';
import { Store } from '@ngrx/store';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { StoryOutputType } from '@src/suite/common/enums/enums';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { selectStoryTemplates, selectTopicRecordByGuid } from '@src/suite/common/store/records/records.selectors';
import { parseMosMedia } from '@src/suite/common/utils/mos-utlis';
import { checkRequiredProperties } from '@src/suite/common/utils/record-utils';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';

import { LoaderComponent } from '../../../loader/loader';
import { StoryFullScreenComponent } from './story-full-screen/story-full-screen';

enum OverlayType {
    ExternalActions = 'ExternalActions',
    AllStoriesDropdown = 'AllStoriesDropdown',
}

@Component({
    selector: 'st-stories-full-screen',
    imports: [LoaderComponent, MatButton, MatDivider, MatRipple, StoryFullScreenComponent],
    templateUrl: './stories-full-screen.html',
    styleUrl: './stories-full-screen.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-stories-full-screen',
    },
})
export class StoriesTabsFullScreenDialogComponent implements AfterViewInit {
    modalService = inject(ModalService);
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    injector = inject(Injector);
    playbookService = inject(PlaybookService);

    StoryOutputType = StoryOutputType;
    isRTL = this.globalTablesService.getRtl();
    OverlayType = OverlayType;
    pstFieldNames = PSTFieldNames;

    readonly saveAndNext = signal(false);

    parentTopic = this.store.selectSignal(selectCurrentStoryParentTopic);
    currentStory = this.store.selectSignal<StorytellingStoryRecord>(selectCurrentRecord);
    selectedStoryIndex = this.store.selectSignal(selectCurrentStoryIndex);
    languageSettings = this.store.selectSignal(selectI18N)();
    storyTemplates = this.store.selectSignal(selectStoryTemplates);
    loaderState = this.store.selectSignal(selectLoaderState);

    /**
     * Checks if the current story is standalone, no matter we are in a 'new record' or not
     */
    readonly standaloneStory = computed(() => this.store.selectSignal(selectIsCurrentStoryStandalone)() || this.store.selectSignal(selectIsCurrentRecordFromPin)());

    /**
     * Auto update is when we are in a 'new record' mode, either we are creating a new topic or a new story
     */
    readonly autoUpdate = computed(() => this.parentTopic()?.isNewRecord || this.currentStory()?.isNewRecord);

    readonly isCurrentRecordNew = computed(() => this.currentStory()?.isNewRecord);

    /**
     * Checks if we are in a 'new record' mode and there is no parent topic. That means we are creating a new loose story
     */
    readonly isStandaloneCreate = computed(() => {
        return this.isCurrentRecordNew() && !this.parentTopic();
    });

    readonly disabled = computed(() => {
        return !this.isRequiredFieldsPopulated() || (!this.isStandaloneCreate() && !this.isRequiredFieldsPopulatedForTopic()) || this.loading() || this.loadingSaveAndNext();
    });
    readonly isRequiredFieldsPopulated = computed(() => {
        return checkRequiredProperties(this.currentStory(), this.playbookService);
    });
    readonly isRequiredFieldsPopulatedForTopic = computed(() => {
        return checkRequiredProperties(this.parentTopic(), this.playbookService);
    });
    readonly loadingSaveAndNext = computed(() => {
        return this.store.selectSignal(selectNewRecordCreationInProgress)() && this.saveAndNext();
    });
    readonly loading = computed(() => {
        return this.store.selectSignal(selectNewRecordCreationInProgress)() && !this.saveAndNext();
    });

    readonly isStoryDisabled = computed(() => {
        return this.store.selectSignal(selectTopicRecordByGuid(this.currentStory()?.PSTStoryTopicGUID))()?.Path?.isWasteBinPath;
    });

    readonly tabsGroup = viewChild<MatTabGroup>(MatTabGroup);

    ngAfterViewInit() {
        this.store.dispatch(PlaybookApiActions.loadStoryTemplates());
    }

    backToTopic() {
        this.store.dispatch(FullscreenActions.closeCurrentStory());
    }

    createStory(stories: MatSelectionList) {
        if (stories.selectedOptions.selected.length === 0) {
            return;
        }
        let templateProgramId: string | null = null;
        const selectedTemplates: StorytellingStoryRecord[] = stories.selectedOptions.selected.map(item => item.value);
        const templateGuids: string[] = selectedTemplates.map(template => template.Guid);
        selectedTemplates
            .filter(template => template.Guid !== OPTION_BLANK)
            .forEach(template => {
                if (template && template.Path && template.Path.fullPath) {
                    templateProgramId = template.Path.fullPath;
                }
            });

        try {
            this.store.dispatch(PlaybookApiActions.createStoryFromTemplate({ topic: this.parentTopic(), templateProgramId: templateProgramId, templateGuids: templateGuids }));
        } catch (ex) {
            console.warn(ex);
        }
        stories.deselectAll();
    }

    createNewStoryVersion(prompt: Prompt) {
        this.store.dispatch(StoryHttpActions.generateNewVersion({ prompt: prompt, topic: this.parentTopic() }));
    }

    onMosMediaClicked(mosId: string) {
        const mosMsg = this.currentStory()?.TextCommands?.[mosId];
        const mosMediaUrls = parseMosMedia(mosMsg);
        if (!mosMediaUrls) return;
        this.store.dispatch(OpenFullscreenModalActions.setFullscreenMediaUrls({ mediaUrls: mosMediaUrls }));
    }

    closeModal() {
        this.store.dispatch(OpenFullscreenModalActions.closeFullscreenModal());
    }

    async create(createAndNext: boolean) {
        if (!this.isRequiredFieldsPopulated()) return;
        this.saveAndNext.set(createAndNext);
        if (this.isStandaloneCreate()) {
            this.store.dispatch(PlaybookApiActions.createStory({ story: this.currentStory(), createAndNext }));
        } else {
            this.store.dispatch(PlaybookApiActions.prepareCreateTopic({ topic: this.parentTopic(), createAndNext }));
        }
    }
}
